import React, { useState } from "react";
import { Banknote, Bike, MapPin, ShieldCheck, Crown, TrendingUp, Package, Award } from "lucide-react";
import { RegisterForm } from "../components/RegisterForm";
import { useI18n } from "../i18n";
import { usePageTitle } from "../lib/usePageTitle";


const BENEFITS = [
  { icon: Banknote, title: "rec.b1t", text: "rec.b1x" },
  { icon: Bike, title: "rec.b2t", text: "rec.b2x" },
  { icon: MapPin, title: "rec.b3t", text: "rec.b3x" },
  { icon: ShieldCheck, title: "rec.b4t", text: "rec.b4x" },
];

function GainSimulator({ t }) {
  const [orders, setOrders] = useState(25);
  const [basket, setBasket] = useState(40);
  const PCT = 0.5;
  const ht = basket / 1.2;
  const productGain = ht * PCT;
  const deliveryGain = 3.5 / 1.2;
  const monthly = Math.round(orders * 4.33 * (productGain + deliveryGain));
  return (
    <div data-testid="gain-simulator" className="border-2 border-neon-green bg-ink-surface p-6 sm:p-8 shadow-[0_0_25px_rgba(157,255,0,0.15)]">
      <h2 className="font-anton text-2xl sm:text-3xl uppercase text-white mb-1">{t("rec.simT")} <span className="neon-text-green">{t("rec.simT2")}</span></h2>
      <p className="text-zinc-500 text-sm mb-6">{t("rec.simSub")}</p>
      <div data-testid="sim-single-rate" className="mb-5 inline-flex items-center gap-2 border border-neon-green bg-neon-green/10 px-3 py-2 text-sm font-bold uppercase text-neon-green">
        <TrendingUp className="h-4 w-4" /> 50 % du HT pour tous les vendeurs
      </div>
      <label className="block text-zinc-300 text-sm mb-1">{t("rec.simOrders")} : <span className="font-anton text-neon-green text-lg">{orders}</span></label>
      <input data-testid="sim-orders" type="range" min="5" max="100" value={orders} onChange={(e) => setOrders(+e.target.value)} className="w-full accent-[#9dff00] mb-5" />
      <label className="block text-zinc-300 text-sm mb-1">{t("rec.simBasket")} : <span className="font-anton text-neon-green text-lg">{basket}€</span></label>
      <input data-testid="sim-basket" type="range" min="20" max="80" step="5" value={basket} onChange={(e) => setBasket(+e.target.value)} className="w-full accent-[#9dff00] mb-4" />
      <p className="text-zinc-500 text-xs mb-4" data-testid="sim-breakdown">
        {t("rec.simBd1")} {productGain.toFixed(2)}€ ({Math.round(PCT * 100)}% {t("rec.simBd2")}) + {deliveryGain.toFixed(2)}€ {t("rec.simBd3")}
      </p>
      <div className="border-t border-zinc-800 pt-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-zinc-500 text-xs uppercase">{t("rec.simResult")}</p>
          <p className="font-anton text-5xl neon-text-green" data-testid="sim-result">{monthly.toLocaleString("fr-FR")}€<span className="text-lg text-zinc-400">/mois</span></p>
        </div>
        <a href="#postuler" className="bg-neon-magenta text-white font-anton uppercase px-6 py-3 glow-magenta hover:bg-neon-purple transition-colors">{t("rec.simCta")}</a>
      </div>
      <p className="text-zinc-600 text-xs mt-4">{t("rec.simNote")}</p>
    </div>
  );
}

export default function Recruitment() {
  const { t } = useI18n();
  usePageTitle(
    "Devenir vendeur CBD — STUFF CBD | Rejoins le réseau",
    "Deviens vendeur CBD indépendant dans ta ville avec STUFF CBD : stock fourni, 50 % du HT pour toi sur chaque vente + frais de livraison, rémunération unique pour tous. Crée ta boutique en 2 minutes."
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-[#030303]">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-neon-green/10 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-10 w-[420px] h-[420px] rounded-full bg-neon-magenta/10 blur-[140px] pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16 w-full flex items-center gap-8">
          <div className="flex-1">
          <div className="inline-flex items-center gap-2 border border-neon-green px-3 py-1 rounded-full mb-5 bg-black/40">
            <Crown className="h-4 w-4 text-neon-green" />
            <span className="text-neon-green text-xs uppercase tracking-widest font-bold">{t("rec.badge")}</span>
          </div>
          <h1 className="font-anton text-5xl sm:text-7xl uppercase text-white leading-none mb-4">
            {t("rec.title1")} <span className="neon-text-green">{t("rec.title2")}</span>
          </h1>
          <p className="text-zinc-200 text-lg max-w-2xl mb-6">
            {t("rec.text1")} <span className="text-neon-magenta font-bold">STUFF</span>{t("rec.text2")}
          </p>
          <a href="#postuler" data-testid="hero-apply-cta" className="inline-block bg-neon-green text-black font-anton uppercase px-8 py-4 glow-green hover:bg-neon-greenDark transition-colors">{t("rec.cta")}</a>
          </div>
          <img src="/lion-livreur.webp" alt="Mascotte STUFF — livreur" className="hidden lg:block w-[560px] xl:w-[660px] object-contain self-center -my-8" loading="eager" />
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-anton text-3xl sm:text-4xl uppercase text-white text-center mb-3">{t("rec.pact1")} <span className="neon-text-magenta">{t("rec.pact2")}</span></h2>
        <p className="text-zinc-500 text-center mb-10">{t("rec.pactSub")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {BENEFITS.map((b, i) => (
            <div key={b.title} className="flex gap-4 bg-ink-surface border border-zinc-800 p-6 hover:border-neon-magenta transition-all">
              <div className="h-12 w-12 shrink-0 flex items-center justify-center border border-neon-magenta rounded-full bg-neon-magenta/10">
                <b.icon className="h-6 w-6 text-neon-magenta" />
              </div>
              <div>
                <h3 className="font-anton text-xl uppercase text-white">{t(b.title)}</h3>
                <p className="text-zinc-400 text-sm mt-1">{t(b.text)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Micro-franchise */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
        <h2 className="font-anton text-3xl sm:text-4xl uppercase text-white text-center mb-3">{t("rec.franch1")} <span className="neon-text-green">{t("rec.franch2")}</span></h2>
        <p className="text-zinc-500 text-center mb-8">{t("rec.franchSub")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-ink-surface border border-neon-green/40 p-6 text-center">
            <TrendingUp className="h-8 w-8 text-neon-green mx-auto mb-3" />
            <div className="font-anton text-4xl text-neon-green">50%</div>
            <p className="text-white font-semibold uppercase text-sm mt-1">{t("rec.stat1t")}</p>
            <p className="text-zinc-500 text-xs mt-1">{t("rec.stat1x")}</p>
          </div>
          <div className="bg-ink-surface border border-neon-magenta/40 p-6 text-center">
            <Package className="h-8 w-8 text-neon-magenta mx-auto mb-3" />
            <div className="font-anton text-4xl text-neon-magenta">0€</div>
            <p className="text-white font-semibold uppercase text-sm mt-1">{t("rec.stat2t")}</p>
            <p className="text-zinc-500 text-xs mt-1">{t("rec.stat2x")}</p>
          </div>
          <div className="bg-ink-surface border border-yellow-400/40 p-6 text-center">
            <Award className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
            <div className="font-anton text-4xl text-yellow-400">1 {t("rec.stat3n")}</div>
            <p className="text-white font-semibold uppercase text-sm mt-1">{t("rec.stat3t")}</p>
            <p className="text-zinc-500 text-xs mt-1">{t("rec.stat3x")}</p>
          </div>
        </div>
      </section>

      {/* Simulateur de gains */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <GainSimulator t={t} />
      </section>

      {/* Étapes */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
        <h2 className="font-anton text-3xl uppercase text-white text-center mb-8">{t("rec.steps1")} <span className="neon-text-magenta">{t("rec.steps2")}</span></h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[["1", t("rec.step1t"), t("rec.step1x")], ["2", t("rec.step2t"), t("rec.step2x")], ["3", t("rec.step3t"), t("rec.step3x")]].map(([n, title, text]) => (
            <div key={n} className="bg-ink-surface border border-zinc-800 p-6">
              <div className="font-anton text-5xl neon-text-green mb-2">{n}</div>
              <h3 className="font-anton text-lg uppercase text-white">{title}</h3>
              <p className="text-zinc-500 text-sm mt-1">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section id="postuler" className="max-w-xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div className="text-center mb-6">
          <Crown className="h-8 w-8 text-neon-magenta mx-auto mb-2" />
          <h2 className="font-anton text-3xl uppercase text-white mb-2">{t("reg.title")}</h2>
          <p className="text-zinc-500 text-sm">{t("reg.sub")}</p>
        </div>
        <RegisterForm />
      </section>
    </div>
  );
}
